/**
 * Dados do painel lateral da sessão. Só formata a leitura que já montou a
 * fila (`counts` de `GET /review/due`) e a fatia seguinte dela. Não reconta
 * cartões, não lê `back` e não chama a API.
 *
 * O tipo de cada próximo sai de `cardTipoMostrado` (design, Decisão 9).
 */
import type { CardLayout, DueCounts, ReviewCard } from "../../../sdk/@types/Revisao";
import { cardTipoMostrado, type CardTipoMostrado } from "./cardTipo";
import type { AtalhoSessao } from "./SessaoControles";

/**
 * O que a lista de próximos mostra: `frente` é o texto da frente do cartão,
 * truncado pela UI — ou o próprio `assunto`, quando a frente vem vazia (o
 * cartão de oclusão, cuja frente é imagem e não texto; design, Decisão 4).
 * `assunto` continua existindo à parte: é só o que `rotuloEdicaoRapida` lê
 * para o rótulo de acessibilidade, que nunca anuncia o conteúdo do cartão.
 * Sem verso, rótulo de marcação ou alternativa.
 */
export interface ProximoCartaoPainel {
  id: string;
  assunto: string;
  frente: string;
  tipo: CardTipoMostrado;
}

export interface SessaoPainelLateralProps {
  /**
   * 1-based, de `sessao.posicao`. Exibir como veio — não derivar de um
   * índice nem de `contagens`.
   */
  posicao: number;
  /**
   * De `sessao.total` (o tamanho da fila carregada). Pode divergir da soma
   * de `contagens` (a fila lida pode trazer menos cartões do que o total
   * vencido naquele estado) — por isso os dois chegam por prop, nunca um
   * derivado do outro.
   */
  total: number;
  /**
   * `counts` da mesma `GET /review/due` que montou a fila. Exibir como veio.
   * Não derivar de `fila.filter`.
   */
  contagens: DueCounts;
  /**
   * Percentual 0–100 vindo dessa mesma leitura. `null` quando ela não trouxe
   * acerto — a tela mostra indisponível, nunca 0 inventado.
   */
  acerto: number | null;
  /**
   * Dias seguidos vindos dessa mesma leitura. `null` quando ela não trouxe
   * a sequência. `0` é um zero real e permanece 0.
   */
  sequencia: number | null;
  /**
   * Variação do acerto, em pontos percentuais, contra o último dia estudado
   * (`ReviewStats.retentionDeltaVsYesterday`). Ausente ou `null` ⇒ a
   * sub-linha simplesmente não renderiza — nunca "0%" inventado. `0` real é
   * uma variação real ("igual a ontem"), distinta de ausência. Opcional
   * porque a leitura que a traz ainda não é feita por toda view que monta
   * este painel.
   */
  acertoDelta?: number | null;
  /**
   * Recorde de sequência, em dias (`ReviewStats.bestStreak`). Ausente ou
   * `null` ⇒ a sub-linha simplesmente não renderiza — nunca "recorde: 0"
   * inventado. Mesma razão de ser opcional que `acertoDelta`.
   */
  sequenciaRecorde?: number | null;
  /** Cartões depois do corrente, já reduzidos a frente, assunto e tipo. */
  proximos: readonly ProximoCartaoPainel[];
  /**
   * Atalhos além dos da sessão. A entrada de editar só vem quando quem estuda
   * administra — omitir deixa a lista sem ela.
   */
  atalhosExtra?: readonly AtalhoSessao[];
  /**
   * Abre a edição rápida do item de "Próximos" cujo id é passado, sem avançar
   * nem reordenar a fila. Presente só quando quem estuda administra **e** o
   * modo admin está ligado — a ausência desta prop é o único gate do selo
   * ADMIN e do botão de cada item; não existe um segundo booleano para
   * manter sincronizado com ela.
   */
  onEditarRapido?: (id: string) => void;
}

/**
 * Campos que identificam o próximo, incluindo `front` para a frente exibida
 * em "Próximos" (design, Decisão 4). `back`, rótulo de marcação e
 * alternativas continuam de fora — a projeção não lê nenhum dos três.
 */
export type CartaoIdentificavel = Pick<
  ReviewCard,
  "id" | "layout" | "subjectId" | "tags" | "front"
> & {
  question?: { subject?: { name?: string } | null } | null;
};

const INDISPONIVEL = "—";

/**
 * Soma única das três contagens que a leitura já trouxe. Não é `fila.length`
 * e não é o que o bloco "Sessão de hoje" mostra como número grande — esse
 * número é o prop `total`. Esta soma serve só de denominador para a
 * proporção visual da barra segmentada.
 */
export function totalDeContagens(contagens: DueCounts): number {
  return contagens.new + contagens.learning + contagens.review;
}

/** A fatia de cada estado entre os três, 0–100. Soma zero vira três zeros, nunca `NaN`. */
export type ProporcaoContagens = { new: number; learning: number; review: number };

/** Só matemática sobre `contagens` — nenhuma leitura, nenhuma segunda contagem sobre a fila. */
export function proporcaoDeContagens(contagens: DueCounts): ProporcaoContagens {
  const soma = totalDeContagens(contagens);
  if (soma <= 0) return { new: 0, learning: 0, review: 0 };
  return {
    new: (contagens.new / soma) * 100,
    learning: (contagens.learning / soma) * 100,
    review: (contagens.review / soma) * 100,
  };
}

/** "12 sobre 40". `posicao` e `total` são exibidos como vieram, nunca recalculados aqui. */
export function textoPosicaoTotal(posicao: number, total: number): string {
  return `${posicao} sobre ${total}`;
}

function numeroPresente(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/** Inteiro fica `82%`. Uma casa em pt-BR fica `82,5%`. Ausente fica `—`. */
export function textoAcerto(acerto: number | null): string {
  if (!numeroPresente(acerto)) return INDISPONIVEL;
  if (Number.isInteger(acerto)) return `${acerto}%`;
  const negative = acerto < 0;
  const tenths = Math.round(Math.abs(acerto) * 10);
  const whole = Math.floor(tenths / 10);
  const frac = tenths % 10;
  return `${negative ? "-" : ""}${whole},${frac}%`;
}

/** `null` fica `—`. `1` fica `1 dia`. Qualquer outro número, inclusive 0, fica `N dias`. */
export function textoSequencia(sequencia: number | null): string {
  if (!numeroPresente(sequencia)) return INDISPONIVEL;
  if (sequencia === 1) return "1 dia";
  return `${sequencia} dias`;
}

/**
 * `null`/ausente devolve `null` — o próprio sinal de "não renderizar a
 * sub-linha", nunca um texto. `0` real é "igual a ontem", uma variação real,
 * nunca confundida com ausência. Mesmo vocabulário de
 * `ConclusaoSessao.formatDelta`: a métrica é a mesma
 * (`retentionDeltaVsYesterday`), só noutra superfície.
 */
export function textoVariacaoAcerto(delta: number | null | undefined): string | null {
  if (!numeroPresente(delta)) return null;
  if (delta === 0) return "igual a ontem";
  const negative = delta < 0;
  const tenths = Math.round(Math.abs(delta) * 10);
  const whole = Math.floor(tenths / 10);
  const frac = tenths % 10;
  const magnitude = frac === 0 ? `${whole}` : `${whole},${frac}`;
  return `${negative ? "-" : "+"}${magnitude} p.p. em relação a ontem`;
}

/**
 * `null`/ausente devolve `null` — a sub-linha não renderiza, nunca
 * "recorde: 0". `1` é singular; qualquer outro número, inclusive 0, é
 * plural, mesma regra de `textoSequencia`.
 */
export function textoRecordeSequencia(recorde: number | null | undefined): string | null {
  if (!numeroPresente(recorde)) return null;
  if (recorde === 1) return "Recorde: 1 dia";
  return `Recorde: ${recorde} dias`;
}

export function acertoIndisponivel(acerto: number | null): boolean {
  return !numeroPresente(acerto);
}

export function sequenciaIndisponivel(sequencia: number | null): boolean {
  return !numeroPresente(sequencia);
}

function textoLimpo(value: string | null | undefined): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * Nome da matéria já resolvido, senão o nome que a questão hidratou, senão
 * a primeira tag. Nunca o id cru e nunca o texto do cartão.
 */
export function assuntoDoCartao(
  card: CartaoIdentificavel,
  subjectNames: Readonly<Record<string, string>>,
): string {
  const named = textoLimpo(subjectNames[card.subjectId]);
  if (named) return named;
  const fromQuestion = textoLimpo(card.question?.subject?.name);
  if (fromQuestion) return fromQuestion;
  const tag = card.tags?.map(textoLimpo).find((item) => item !== "");
  if (tag) return tag;
  return "Sem assunto";
}

export function proximoDaFila(
  card: CartaoIdentificavel,
  subjectNames: Readonly<Record<string, string>>,
): ProximoCartaoPainel {
  const layout: CardLayout | undefined = card.layout;
  const assunto = assuntoDoCartao(card, subjectNames);
  return {
    id: card.id,
    assunto,
    frente: textoLimpo(card.front) || assunto,
    tipo: cardTipoMostrado({ layout }),
  };
}

/** Rótulo acessível do botão de edição rápida — só assunto e tipo, nunca conteúdo do cartão. */
export function rotuloEdicaoRapida(item: ProximoCartaoPainel): string {
  return `Editar rapidamente: ${item.assunto} — ${item.tipo}`;
}

/** Depois do corrente. O cartão na mão é o baralho, não um "próximo". */
export function proximosDaFila(
  fila: readonly CartaoIdentificavel[],
  indice: number,
  subjectNames: Readonly<Record<string, string>>,
): ProximoCartaoPainel[] {
  if (indice < 0) return [];
  return fila.slice(indice + 1).map((card) => proximoDaFila(card, subjectNames));
}
